import { useSearchParams } from "react-router";
import { Button, EmptyState, formatDateTime } from "@omnabase/ui";
import type { UnknownMailList } from "@omnabase/shared";
import { useFetch, useTags, useTicketCounts } from "../../lib/inbox";
import { useCan } from "../../lib/access";
import { useNav, useSetParam, useWs } from "../../lib/nav";
import { Shell } from "../../shell/Shell";
import { InboxColumn } from "./InboxColumn";

/** "Unknown address" (the ticket page and marks round, 2026-10-23, brief item 7): the mail that reached this workspace's subdomain at an
 *  address no open project has, for its administrators, newest first, 50 to a page (`?page=`): when, who sent it, where it went, the subject.
 *  No action on a row for now: the founder wants to see during the beta how often it happens. The rows go after 90 days. */
const PAGE = 50;
export function UnknownAddressScreen() {
  const nav = useNav(); const { ws, slug } = useWs(); const setParam = useSetParam();
  const [params] = useSearchParams();
  const admin = useCan()("team");
  const page = Math.max(0, Number.parseInt(params.get("page") ?? "0", 10) || 0);
  const list = useFetch<UnknownMailList>(admin ? `/api/v1/w/${slug}/unknown-mail?offset=${page * PAGE}&limit=${PAGE}` : null);
  const counts = useTicketCounts(slug);
  const tags = useTags(slug);
  const total = list.data?.total ?? 0, rows = list.data?.rows ?? [];
  const crumbs = [{ label: "Tickets", href: ws("/inbox/queue") }, { label: "Unknown address", note: total ? `${total} ${total === 1 ? "mail" : "mails"}` : undefined, phoneCount: total ? String(total) : undefined, href: ws("/inbox/unknown-address"), views: true }];
  return (
    <Shell views={<InboxColumn view="unknown-address" tagId={null} projectId={null} counts={counts} tags={tags.data ?? []} viewHref={(id) => ws(`/inbox/${id}`)} tagHref={(id) => ws(`/inbox/all-open?tag=${id}`)} onNavigate={nav} />} crumbs={crumbs} bodyClassName="ob-body--dashboard">
      <section className="ob-listpage" aria-label="Unknown address">
        <div className="ob-listpage__head">
          <h1 className="ob-listpage__title">Unknown address</h1>
          <span className="ob-listpage__count">{list.data ? String(total) : ""}</span>
        </div>
        <p className="ob-listpage__intro">Mail sent to an address on this workspace that no project uses. Kept for 90 days; spam and automatic replies are left out.</p>
        {!admin ? <EmptyState icon="lock" title="For administrators" text="Only workspace administrators see mail sent to unknown addresses." /> : (
          <div className="ob-listpage__scroll ob-dtable-card">
            <table className="ob-dtable" aria-label="Unknown address">
              <thead><tr><th>Date</th><th>Sender</th><th className="ob-dtable__col--phone">Sent to</th><th>Subject</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="ob-dtable__muted ob-dtable__nowrap">{formatDateTime(r.receivedAt)}</td>
                    <td><span className="ob-dtable__name" title={r.from}>{r.from || "Unknown"}</span></td>
                    <td className="ob-dtable__muted ob-dtable__col--phone"><span title={r.to}>{r.to}</span></td>
                    <td className="ob-dtable__grow"><span className="ob-dtable__name" title={r.subject}>{r.subject || "(no subject)"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.data && rows.length === 0 && <EmptyState icon="mail" title="No mail to unknown addresses" text="Mail sent to an address no project uses appears here." />}
          </div>
        )}
        {total > PAGE && (
          <div className="ob-listpage__pager">
            <Button variant="ghost" size="sm" icon="back" disabled={page === 0} onClick={() => setParam("page", page > 1 ? String(page - 1) : null)}>Newer</Button>
            <span className="ob-listpage__count">{page * PAGE + 1}–{Math.min(total, (page + 1) * PAGE)} of {total}</span>
            <Button variant="ghost" size="sm" icon="arrowForward" disabled={(page + 1) * PAGE >= total} onClick={() => setParam("page", String(page + 1))}>Older</Button>
          </div>
        )}
      </section>
    </Shell>
  );
}
